import { Drawer, Typography, Box, LinearProgress } from "@mui/material";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ButtonsData,
  DownloadBillsListRequestBody,
  FiltersBills,
  SelectedRows,
} from "../../../core/models/bills/bills.model";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import {
  chunkArray,
  downloadBillsDocument,
  getBillsIdDocumentsListRequestBody,
  getDownloadBillsListRequestBody,
} from "../../../utils/components/bills/bills.util";
import {
  downloadBillsList,
  getBillsIdDocumentsList,
} from "../../../core/services/bills/bills.service";
import { v4 as uuidv4 } from "uuid";
import "./BillsDownload.scss";
import { GroupedSuppliesTableRow } from "../../../core/models/supplies/supplies.model";

export interface BillsDownloadProps {
  isDownloadDrawerOpen: boolean;
  isSelectedDownloader: boolean;
  setIsDownloadDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSelectedDownloader: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRows: SelectedRows[];
  filtersBills: FiltersBills;
  selectedSupply: GroupedSuppliesTableRow | null;
  isFilterApplied: boolean;
}

const BillsDownload = ({
  isDownloadDrawerOpen,
  isSelectedDownloader,
  setIsDownloadDrawerOpen,
  setIsSelectedDownloader,
  selectedRows,
  filtersBills,
  selectedSupply,
  isFilterApplied,
}: BillsDownloadProps) => {
  const { t } = useTranslation();
  const { ownerData, userData, setSnackbarData } =
    useContext(TotalEnergiesContext);
  const [showProgressBar, setShowProgressBar] = useState<boolean>(false);
  const [progress, setProgress] = useState(0);
  const [isAborted, setIsAborted] = useState(false);
  const [billsList, setBillsList] = useState<string[][]>([]);
  const [fileType, setFileType] = useState<string>("");
  const [count, setCount] = useState<number>(0);
  const [isFlying, setIsFlying] = useState<boolean>(false);
  const [requestId, setRequestId] = useState<string>("");
  const getBillsIdDocumentsListAbort = new AbortController();

  const handleClose = useCallback(() => {
    setIsDownloadDrawerOpen(false);
    setIsSelectedDownloader(false);
    setShowProgressBar(false);
    setIsFlying(false);
    setBillsList([]);
  }, [setIsDownloadDrawerOpen, setIsSelectedDownloader]);

  const getDownloadBillsList = useCallback(
    async (
      billsIdList: Array<string[]>,
      progressStep: number,
      fileType: string,
      requestId: string,
      count: number,
      abortSignal: AbortSignal
    ) => {
      const requestBody: DownloadBillsListRequestBody =
        getDownloadBillsListRequestBody(
          requestId,
          billsIdList.length,
          count,
          fileType,
          billsIdList[count]
        );

      downloadBillsList(requestBody, userData?.token || "", abortSignal)
        .then(({ finished, downloadLinks }) => {
          setCount(count + 1);
          if (finished === "true") {
            downloadLinks.forEach((downloadLink, index) => {
              setTimeout(() => {
                const link = document.createElement("a");
                if (link.download !== undefined) {
                  downloadBillsDocument(downloadLink.downloadLink, link);
                  const progress = progressStep * (count + 1 + (index + 1));
                  setProgress(progress >= 100 ? 100 : progress);
                }
              }, index * 1000);
            });
          } else setProgress(progressStep * (count + 1));

          setIsFlying(false);
        })
        .catch((error) => {
          handleClose();
          setSnackbarData({
            isOpen: true,
            isSuccessSeverity: false,
            text: "Ha habido un error con la descarga",
          });
        });
    },
    [handleClose, setSnackbarData, userData?.token]
  );

  useEffect(() => {
    if (progress === 100) {
      const timeOut = setTimeout(() => {
        handleClose();
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: true,
          text: "Descarga realizada con éxito",
        });
        clearTimeout(timeOut);
      }, 500);
    }
  }, [handleClose, progress, setSnackbarData]);

  useEffect(() => {
    const getBillsDownloadListAbort = new AbortController();

    if (
      billsList.length &&
      !isFlying &&
      !isAborted &&
      count <= billsList.length - 1
    ) {
      const progressStep = 100 / (billsList.length * 2);
      getDownloadBillsList(
        billsList,
        progressStep,
        fileType,
        requestId,
        count,
        getBillsDownloadListAbort.signal
      );
      setIsFlying(true);
    }
  }, [
    billsList,
    count,
    fileType,
    getDownloadBillsList,
    isAborted,
    isFlying,
    progress,
    requestId,
  ]);

  const buttonsData: ButtonsData[] = [
    {
      id: "pdf",
      label: "Descargar PDF",
      value: "pdf",
    },
    {
      id: "xls",
      label: "Descargar Excel",
      value: "xls",
    },
  ];

  const handleClick = (fileType: string) => {
    handleClickDownloadReset();
    const chunkSize = fileType === "pdf" ? 2 : 100;
    setFileType(fileType);

    setRequestId(uuidv4());

    if (isSelectedDownloader) {
      const billsIdList = chunkArray(
        selectedRows.map(
          (selectedRow) => selectedRow[fileType === "pdf" ? "id" : "idExcel"]
        ),
        chunkSize
      );
      setBillsList(billsIdList);
      return;
    }
    handleClickDownloadAll(chunkSize, fileType);
  };

  const handleClickDownloadAll = (chunkSize: number, fileType: string) => {
    const requestBody = getBillsIdDocumentsListRequestBody(
      ownerData,
      filtersBills,
      selectedSupply
    );

    getBillsIdDocumentsList(
      requestBody,
      userData?.token || "",
      getBillsIdDocumentsListAbort.signal
    )
      .then(
        async ({
          Gas: {
            IdsDocumento: documentsGasIds,
            IdsFacturaVentaCabecera: excelDocumentsGasIds,
          },
          Luz: {
            IdsDocumento: documentsLightsIds,
            IdsFacturaVentaCabecera: excelDocumentsLightIds,
          },
        }) => {
          const ids =
            fileType === "pdf"
              ? [...documentsGasIds, ...documentsLightsIds]
              : [...excelDocumentsGasIds, ...excelDocumentsLightIds];
          const billsIdList = chunkArray(ids, chunkSize);

          setBillsList(billsIdList);
        }
      )
      .catch(() => {
        handleClose();
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: false,
          text: "Ha habido un error con la descarga",
        });
      });
  };

  const handleClickDownloadReset = () => {
    setShowProgressBar(true);
    setIsAborted(false);
    setProgress(0);
    setBillsList([]);
    setCount(0);
  };

  return (
    <Drawer
      className={`bills-download__drawer`}
      open={isDownloadDrawerOpen}
      anchor="bottom"
      PaperProps={{
        sx: { padding: "3rem  5.5rem" },
        className: "bills-download",
      }}
    >
      <Typography component="h3" variant="h3">
        {t("Facturas Página.Elige un formato de descarga")}
      </Typography>
      <Typography component="p" variant="body2">
        {t(
          "Facturas Página.Debes tener en cuenta que la descarga de un volumen alto de facturas puede conllevar mucho tiempo."
        )}
      </Typography>
      {!!isSelectedDownloader && (
        <Typography component="p" variant="subtitle1" fontWeight={400}>
          {selectedRows.length}{" "}
          {t(
            `Facturas Página.${
              selectedRows.length === 1
                ? "Factura seleccionada"
                : "Facturas seleccionadas"
            }`
          )}
        </Typography>
      )}
      {showProgressBar ? (
        <Box component="div" className="bills-download__progress">
          <Typography component="caption">
            {t("Facturas Página.Esto puede llevar unos minutos")}...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            className="progress"
          />
          <ButtonElement
            onClick={() => {
              getBillsIdDocumentsListAbort.abort();
              setIsAborted(true);
              handleClose();
            }}
            link
          >
            {t("Facturas Página.Cancelar descarga")}
          </ButtonElement>
        </Box>
      ) : (
        <Box
          component="div"
          display="flex"
          justifyContent="space-between"
          className="bills-download__buttons"
        >
          <Box component="div" display="flex" gap="1.5rem">
            {buttonsData.map(({ id, label, value }) => (
              <ButtonElement
                key={id}
                secondary
                iconL
                onClick={() => handleClick(value)}
                iconName="t-icon-pdf"
              >
                {t(`Facturas Página.${label}`)}
              </ButtonElement>
            ))}
          </Box>
          <ButtonElement onClick={handleClose} link>
            {t("Facturas Página.Cancelar")}
          </ButtonElement>
        </Box>
      )}
    </Drawer>
  );
};

export default BillsDownload;
